import React, { useEffect, useRef } from 'react';
import * as d3 from 'd3';
import { useTheme } from '../../theme/ThemeContext';

function hexToRgb(hex) {
    const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
    return result ? {
        r: parseInt(result[1], 16),
        g: parseInt(result[2], 16),
        b: parseInt(result[3], 16)
    } : null;
}
export default function UMAPScatterplot({ data, possibleColorDict }) {
    const containerRef = useRef();
    const svgRef = useRef();
    const { theme } = useTheme();

    const drawChart = () => {
        if (!data || !containerRef.current) return;

        d3.select(svgRef.current).selectAll('*').remove();

        const containerWidth = containerRef.current.clientWidth;
        const containerHeight = containerWidth * 0.6;
        const margin = { top: 40, right: 40, bottom: 60, left: 60 };
        const width = containerWidth - margin.left - margin.right;
        const height = containerHeight - margin.top - margin.bottom;

        const svg = d3.select(svgRef.current)
            .attr('width', containerWidth)
            .attr('height', containerHeight)
            .append('g')
            .attr('transform', `translate(${margin.left},${margin.top})`);

        // Scales
        const xScale = d3.scaleLinear()
            .domain(d3.extent(data, d => d.x))
            .range([0, width])
            .nice();

        const yScale = d3.scaleLinear()
            .domain(d3.extent(data, d => d.y))
            .range([height, 0])
            .nice();

        // axes
        svg.append('g')
            .attr('transform', `translate(0,${height})`)
            .call(d3.axisBottom(xScale))
            .attr('color', theme.colors.text);

        svg.append('g')
            .call(d3.axisLeft(yScale))
            .attr('color', theme.colors.text);

        // points with colors from possibleColorDict
        svg.selectAll('circle')
            .data(data)
            .enter()
            .append('circle')
            .attr('cx', d => xScale(d.x))
            .attr('cy', d => yScale(d.y))
            .attr('r', 4)
            .attr('fill', d => {
                if (d.color_hex) {
                    // Convert hex to RGB
                    const rgb = hexToRgb(d.color_hex);
                    return rgb ? `rgb(${rgb.r}, ${rgb.g}, ${rgb.b})` : '#ccc';
                }
                const color = possibleColorDict[d.biome];
                return color ? `rgb(${color[0]}, ${color[1]}, ${color[2]})` : '#ccc';
            })
            .attr('opacity', 0.6)
            .style('cursor', 'pointer');

        // tooltip
        const tooltip = d3.select(containerRef.current)
            .append('div')
            .attr('class', 'tooltip')
            .style('opacity', 0)
            .style('position', 'absolute')
            .style('background-color', theme.colors.surface)
            .style('color', theme.colors.text)
            .style('padding', '10px')
            .style('border-radius', '5px')
            .style('pointer-events', 'none')
            .style('z-index', 100);

        // tooltip interactions
        let isHovering = false;

        const showTooltip = (event, d) => {
            const containerRect = containerRef.current.getBoundingClientRect();
            const circleRect = event.target.getBoundingClientRect();

            tooltip
                .style('opacity', 0.9)
                .html(`
                    <strong>Sample:</strong> ${d.sampleId}<br/>
                    <strong>Biome:</strong> ${d.biome.replace(/_/g, ' ').replace(/:/g, ' ').replace(/root/g, ' ')}
                `);

            const tooltipRect = tooltip.node().getBoundingClientRect();
            let tooltipX = (circleRect.left - containerRect.left) + circleRect.width + 10;
            let tooltipY = (circleRect.top - containerRect.top) + 10;

            const maxLeft = containerRect.width - tooltipRect.width;
            const maxTop = containerRect.height - tooltipRect.height;

            tooltipX = Math.min(tooltipX, maxLeft);
            tooltipY = Math.min(Math.max(tooltipY, 0), maxTop);

            tooltip
                .style('left', `${tooltipX}px`)
                .style('top', `${tooltipY}px`);
        };

        svg.selectAll('circle')
            .on('mouseover', (event, d) => {
                isHovering = true;
                showTooltip(event, d);
            })
            .on('mouseout', () => {
                isHovering = false;
                setTimeout(() => {
                    if (!isHovering) {
                        tooltip.transition().duration(200).style('opacity', 0);
                    }
                }, 50);
            });

        // labels
        svg.append('text')
            .attr('x', width / 2)
            .attr('y', height + 40)
            .attr('text-anchor', 'middle')
            .attr('fill', theme.colors.text)
            .text('UMAP 1');

        svg.append('text')
            .attr('transform', 'rotate(-90)')
            .attr('x', -height / 2)
            .attr('y', -40)
            .attr('text-anchor', 'middle')
            .attr('fill', theme.colors.text)
            .text('UMAP 2');
    };

    useEffect(() => {
        drawChart();
        const handleResize = () => drawChart();
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, [data, theme, possibleColorDict]);

    return (
        <div ref={containerRef} style={{ width: '100%', position: 'relative' }}>
            <svg ref={svgRef} />
        </div>
    );
}